import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import AppLayout from "../components/AppLayout";
import api, { formatError } from "../lib/api";
import { fmtDT } from "../lib/outreach";

export default function ApproveLinkPage() {
  const { id } = useParams();
  const [state, setState] = useState({ phase: "loading" });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      let email = null;
      try {
        ({ data: email } = await api.get(`/outreach/emails/${id}`));
        if (email.status !== "Draft") return setState({ phase: "already", email });
        const { data: approved } = await api.post(`/outreach/emails/${id}/approve`);
        setState({ phase: "approved", email: { ...email, ...approved } });
      } catch (err) {
        setState({ phase: "error", email, error: formatError(err.response?.data?.detail) });
      }
    })();
  }, [id]);

  const { phase, email, error } = state;
  return (
    <AppLayout>
      <div data-testid="approve-link-page" className="p-4 sm:p-8 max-w-2xl mx-auto animate-fade-in">
        <div className="rounded-2xl border border-slate-800 bg-[#131C31] p-8">
          {phase === "loading" && <p className="text-slate-400 inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Approving…</p>}
          {phase === "approved" && (
            <div data-testid="approve-link-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-3" />
              <h1 className="font-display font-black text-2xl mb-1">Approved</h1>
              <p className="text-slate-300 text-sm">Email {email.step} to <b>{email.company_name}</b> ({email.to}) is scheduled for <b data-testid="approve-link-scheduled">{fmtDT(email.scheduled_at)}</b></p>
            </div>
          )}
          {phase === "already" && (
            <div data-testid="approve-link-already">
              <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
              <h1 className="font-display font-black text-2xl mb-1">Nothing to approve</h1>
              <p className="text-slate-300 text-sm">Email {email.step} to <b>{email.company_name}</b> is already <b>{email.status}</b>.</p>
            </div>
          )}
          {phase === "error" && (
            <div data-testid="approve-link-error">
              <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
              <h1 className="font-display font-black text-2xl mb-1">Couldn't approve</h1>
              <p className="text-slate-300 text-sm">{error}</p>
            </div>
          )}
          {email && (
            <div className="mt-5 rounded-lg border border-slate-800 bg-[#0B1120] p-4">
              <p className="text-sm font-semibold text-white mb-2">{email.subject}</p>
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-300 max-h-64 overflow-auto">{email.body}</pre>
            </div>
          )}
          {phase !== "loading" && (
            <div className="flex flex-wrap gap-3 mt-6">
              {email && <Link data-testid="approve-link-open-lead" to={`/app/leads/${email.lead_id}`} className="rounded-lg px-4 py-2 text-sm border border-slate-700 text-slate-300 hover:border-amber-500/50">Open lead</Link>}
              <Link data-testid="approve-link-open-outreach" to="/app/outreach" className="btn-primary rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2">Review other drafts <ArrowRight className="w-4 h-4" /></Link>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
